import React, { useEffect, useRef } from 'react';
import { Box, useTheme } from '@ublend-npm/aulaui-core';
import { createStyles } from './CommonRoomResponsive.styles';
import CommonRoomContent from './CommonRoomContent';
import CommonRoomFeedResponsive from './CommonRoomFeedResponsive';
import CommonRoomSidebar from './CommonRoomSidebar';
import ScheduledPostList from './ScheduledPost/ScheduledPostList.container';
import type { CommonRoomView } from './types';
import type { Post } from '@app/types/core';
import type { ParticipantsPreviewParticipant } from './ParticipantsPreview/ParticipantsPreview.types';
import type { CreatePostAuthor } from '../Shared/ComposePost/ComposePost';

interface CommonRoomResponsiveProps {
  fetchParticipantsPreview?: () => void;
  isFetchingParticipants?: boolean;
  participantTotal?: number;
  participants?: ParticipantsPreviewParticipant[];
  spaceId?: string;
  view?: CommonRoomView;
  posts?: Post[];
  author?: CreatePostAuthor;
  readOnly?: boolean;
}

const CommonRoomResponsive = ({
  fetchParticipantsPreview,
  isFetchingParticipants = false,
  participantTotal = 0,
  participants = [],
  spaceId,
  view = 'feed',
  posts = [],
  author,
  readOnly = false,
}: CommonRoomResponsiveProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const requestedParticipantsForSpace = useRef<string>();

  useEffect(() => {
    if (
      !spaceId ||
      participantTotal === 0 ||
      participants.length >= Math.min(participantTotal, 4) ||
      isFetchingParticipants ||
      !fetchParticipantsPreview ||
      requestedParticipantsForSpace.current === spaceId
    ) {
      return;
    }

    requestedParticipantsForSpace.current = spaceId;
    fetchParticipantsPreview();
  }, [
    fetchParticipantsPreview,
    isFetchingParticipants,
    participantTotal,
    participants.length,
    spaceId,
  ]);

  return (
    <Box sx={styles.root} data-testid="common-room-responsive">
      <CommonRoomSidebar
        participantTotal={participantTotal}
        participants={participants}
        spaceId={spaceId}
      />

      <CommonRoomContent
        label={
          view === 'scheduled'
            ? 'Scheduled posts content'
            : 'Common Room content'
        }
      >
        {view === 'scheduled' ? (
          <ScheduledPostList />
        ) : (
          <CommonRoomFeedResponsive
            posts={posts}
            author={author}
            readOnly={readOnly}
          />
        )}
      </CommonRoomContent>
    </Box>
  );
};

export default CommonRoomResponsive;
